// Resize+recompress an image in the browser before upload, to keep SharePoint
// storage small (a ~4MB phone photo -> ~300-500KB). Non-images pass through.
//
// We respect EXIF orientation (so portrait phone photos don't land sideways),
// scale the longest side down to `maxDim` (never up), and re-encode as JPEG.
// Any failure (unsupported codec, blocked canvas, etc.) falls back to the
// original file so an upload is never lost to compression.
//
// ⚠️ The re-encode drops every EXIF tag, capture time and GPS included. Read
// anything you need from the ORIGINAL first — see lib/photoMeta.ts.

/** Strip the extension off a filename, e.g. "IMG_0042.HEIC" -> "IMG_0042". */
function stripExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

export interface CompressedImage {
  file: File;
  /** True when `file` is a freshly encoded JPEG; false when it is the original,
   *  passed through (not an image, or the browser couldn't decode it). */
  reencoded: boolean;
  /** Pixel size of `file`, or null when it passed through uncompressed. */
  width: number | null;
  height: number | null;
}

/**
 * The extension to name an uploaded file with: `jpg` when it was re-encoded,
 * otherwise the ORIGINAL's — a HEIC the browser can't decode passes through
 * unchanged, and naming those bytes `.jpg` leaves a file SharePoint can't
 * preview and a download that won't open (review of #560). Letters and digits
 * only, so a name can't smuggle a path or SharePoint-invalid character.
 */
export function uploadExtension(original: File, reencoded: boolean): string {
  if (reencoded) return 'jpg';
  const fromName = /\.([A-Za-z0-9]{1,5})$/.exec(original.name)?.[1];
  if (fromName) return fromName.toLowerCase();
  const fromType = /^image\/([a-z0-9]{1,5})$/.exec(original.type)?.[1];
  return fromType === 'jpeg' ? 'jpg' : fromType ?? 'bin';
}

export async function compressImage(
  file: File,
  maxDim = 1920,
  quality = 0.82,
): Promise<File> {
  return (await compressImageSized(file, maxDim, quality)).file;
}

/** compressImage, plus the output's pixel size. */
export async function compressImageSized(
  file: File,
  maxDim = 1920,
  quality = 0.82,
): Promise<CompressedImage> {
  const passThrough: CompressedImage = { file, reencoded: false, width: null, height: null };
  // Non-images pass through untouched.
  if (!file.type.startsWith('image/')) return passThrough;

  try {
    // imageOrientation:'from-image' bakes EXIF rotation into the bitmap so the
    // canvas draw is already upright.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    const { width, height } = bitmap;
    if (!width || !height) {
      bitmap.close?.();
      return passThrough;
    }

    // Scale so the longest side <= maxDim; never upscale (scale capped at 1).
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return passThrough;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
    );
    if (!blob) return passThrough;

    return {
      file: new File([blob], `${stripExt(file.name)}.jpg`, { type: 'image/jpeg' }),
      reencoded: true,
      width: targetW,
      height: targetH,
    };
  } catch {
    // Anything unexpected → return the original file unchanged.
    return passThrough;
  }
}
